import { AgGrid } from "@jsq/ds/core";
import { IconType } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { ComplianceCaseResolution, ComplianceCaseStatus } from "~/graphql/supergraphEnums";
import { mapCaseTypeToText } from "../CaseTypeRenderer/utils";
export const mapStatusToText = (status: ComplianceCaseStatus | string) => {
  switch (status) {
    case ComplianceCaseStatus.InProgress:
      return {
        displayText: "In progress",
        iconType: "sync-alt" as IconType,
      };
    case ComplianceCaseStatus.Closed:
      return {
        displayText: "Closed",
        iconType: "flag-checkered" as IconType,
        iconColor: Colors.Neutral900,
      };
    case ComplianceCaseStatus.PendingReview:
      return {
        displayText: "Pending review",
        iconType: "magnifying-glass-plus" as IconType,
      };
    case ComplianceCaseStatus.Ready:
      return {
        displayText: "Ready for review",
        iconType: "user-plus" as IconType,
      };
    case ComplianceCaseStatus.UnderReview:
      return {
        displayText: "In review",
        iconType: "search" as IconType,
      };
    case ComplianceCaseStatus.Escalated:
      return {
        displayText: "Pending CO review",
        iconType: "search" as IconType,
      };
    case ComplianceCaseStatus.PendingDataSubmission:
      return {
        displayText: "Pending AML data/doc submission",
        iconType: "hourglass-start" as IconType,
      };
    case ComplianceCaseStatus.PendingFundReview:
      return {
        displayText: "Pending GP review",
        iconType: "hourglass-start" as IconType,
      };
    case ComplianceCaseStatus.PendingSubmission:
      return {
        displayText: "Pending subscription submission",
        iconType: "hourglass-start" as IconType,
      };
    case ComplianceCaseStatus.PendingDataResubmission:
      return {
        displayText: "Pending data/doc resubmission",
        iconType: "hourglass-start" as IconType,
      };
    case ComplianceCaseStatus.NotReady:
      return {
        displayText: "LP request in progress",
        iconType: "hourglass-start" as IconType,
      };
    case "deferred":
      return {
        displayText: "Deferred",
        iconType: "hourglass-clock" as IconType,
      };

    case "":
      return {
        displayText: "",
      };
    default:
      return {
        displayText: "Invalid status",
        iconType: "times-circle" as IconType,
      };
  }
};

export const sortStrings = (a: string, b: string) => {
  return a.toLowerCase().localeCompare(b.toLowerCase());
};
export const sortDate = (a: Date, b: Date) => {
  return new Date(a).getTime() - new Date(b).getTime();
};

export const statusValueFormatter = (params: AgGrid.ValueFormatterParams) => {
  const value = params.value;
  return mapStatusToText(value).displayText;
};

export const caseTypeValueFormatter = (params: AgGrid.ValueFormatterParams) => {
  const value = params.value;
  return mapCaseTypeToText(value);
};

export const parseCaseIdsFromUrl = (search: string): number[] | undefined => {
  return new URLSearchParams(search)
    .get("caseIds")
    ?.split(",")
    .map((id) => parseInt(id.trim(), 10))
    .filter((id) => !isNaN(id));
};

export const resolutionMap: Record<
  ComplianceCaseResolution | string,
  { displayText: string; iconType?: IconType; iconColor?: Colors }
> = {
  // Investor Onboarding case resolutions
  [ComplianceCaseResolution.IoPendingDataSubmission]: {
    displayText: "Missing data/docs",
    iconType: "file-edit",
  },
  [ComplianceCaseResolution.IoReadyResubmitted]: {
    displayText: "LP data/docs resubmitted",
    iconType: "file-download",
  },
  // Reopened cases
  [ComplianceCaseResolution.IoReadyReopened]: {
    displayText: "Re-opened case",
    iconType: "folder-open",
  },
  [ComplianceCaseResolution.SofReadyReopened]: {
    displayText: "Re-opened case",
    iconType: "folder-open",
  },
  [ComplianceCaseResolution.TmReadyReopened]: {
    displayText: "Re-opened case",
    iconType: "folder-open",
  },
  [ComplianceCaseResolution.IoReadyRestarted]: {
    displayText: "Data/doc change detected",
    iconType: "file-exclamation",
  },
  [ComplianceCaseResolution.IoReadyPendingFundEscalation]: {
    displayText: "Requires GP review",
    iconType: "exclamation-triangle",
  },
  [ComplianceCaseResolution.IoReadyPendingResubmission]: {
    displayText: "Requires LP data/doc submission",
    iconType: "file-edit",
  },
  [ComplianceCaseResolution.TmReadyEscalated]: {
    displayText: "Requires CO review",
    iconType: "exclamation-triangle",
  },
  // In progress cases
  [ComplianceCaseResolution.IoInProgress]: {
    displayText: "Assigned",
    iconType: "user",
  },
  [ComplianceCaseResolution.SofInProgress]: {
    displayText: "Assigned",
    iconType: "user",
  },
  [ComplianceCaseResolution.TmInProgress]: {
    displayText: "Assigned",
    iconType: "user",
  },
  [ComplianceCaseResolution.IoInProgressDeEscalated]: {
    displayText: "De-escalated",
    iconType: "arrow-down",
  },
  // Returned to maker cases
  [ComplianceCaseResolution.IoInProgressReturned]: {
    displayText: "Returned",
    iconType: "user",
  },
  [ComplianceCaseResolution.SofInProgressReturned]: {
    displayText: "Returned",
    iconType: "user",
  },
  [ComplianceCaseResolution.TmInProgressReturned]: {
    displayText: "Returned",
    iconType: "user",
  },
  [ComplianceCaseResolution.IoInProgressResubmitted]: {
    displayText: "LP data/docs resubmitted",
    iconType: "file-download",
  },
  [ComplianceCaseResolution.IoPendingReview]: {
    displayText: "Proposed complete",
    iconType: "user-check",
  },
  [ComplianceCaseResolution.IoPendingReviewEscalated]: {
    displayText: "Escalated to GP",
    iconType: "arrow-up",
    iconColor: Colors.Danger600,
  },
  [ComplianceCaseResolution.IoUnderReview]: {
    displayText: "Proposed complete",
    iconType: "user-check",
  },
  [ComplianceCaseResolution.IoPendingReviewAccepted]: {
    displayText: "GP verdict rendered",
    iconType: "badge-check",
  },
  [ComplianceCaseResolution.IoPendingReviewRejected]: {
    displayText: "GP verdict rendered",
    iconType: "badge-check",
  },
  [ComplianceCaseResolution.IoUnderReviewAccepted]: {
    displayText: "GP verdict rendered",
    iconType: "badge-check",
  },
  [ComplianceCaseResolution.IoUnderReviewRejected]: {
    displayText: "GP verdict rendered",
    iconType: "badge-check",
  },
  [ComplianceCaseResolution.IoPendingReviewProposedDeficient]: {
    displayText: "Proposed LP data/doc collection",
    iconType: "user-magnifying-glass",
  },
  [ComplianceCaseResolution.IoUnderReviewProposedDeficient]: {
    displayText: "Proposed LP data/doc collection",
    iconType: "user-magnifying-glass",
  },
  [ComplianceCaseResolution.IoPendingReviewProposedFundEscalation]: {
    displayText: "Proposed GP escalation",
    iconType: "user-magnifying-glass",
  },
  [ComplianceCaseResolution.IoUnderReviewProposedFundEscalation]: {
    displayText: "Proposed GP escalation",
    iconType: "user-magnifying-glass",
  },
  [ComplianceCaseResolution.IoPendingReviewProposedInvalid]: {
    displayText: "Proposed invalid",
    iconType: "ban",
  },
  [ComplianceCaseResolution.IoUnderReviewProposedInvalid]: {
    displayText: "Proposed invalid",
    iconType: "ban",
  },
  [ComplianceCaseResolution.IoPendingReviewProposedWaived]: {
    displayText: "Proposed waived",
    iconType: "ban",
  },
  [ComplianceCaseResolution.IoUnderReviewProposedWaived]: {
    displayText: "Proposed waived",
    iconType: "ban",
  },
  [ComplianceCaseResolution.IoPendingReviewProposedDeferred]: {
    displayText: "Proposed deferred",
    iconType: "hourglass-clock",
  },
  [ComplianceCaseResolution.IoUnderReviewProposedDeferred]: {
    displayText: "Proposed deferred",
    iconType: "hourglass-clock",
  },
  [ComplianceCaseResolution.IoDeferred]: {
    displayText: "Compliance deferred",
    iconType: "hourglass-clock",
  },
  [ComplianceCaseResolution.IoPendingFundReview]: {
    displayText: "Escalated to GP",
    iconType: "exclamation-triangle",
    iconColor: Colors.Danger600,
  },
  [ComplianceCaseResolution.IoPendingDataResubmission]: {
    displayText: "LP request in progress",
    iconType: "file-edit",
  },
  [ComplianceCaseResolution.IoClosed]: {
    displayText: "Approved",
    iconType: "check-circle",
    iconColor: Colors.Success600,
  },
  [ComplianceCaseResolution.IoClosedAccepted]: {
    displayText: "GP verdict: approved",
    iconType: "check-circle",
    iconColor: Colors.Success600,
  },
  [ComplianceCaseResolution.IoClosedRejected]: {
    displayText: "GP verdict: rejected",
    iconType: "xmark",
    iconColor: Colors.Danger600,
  },
  // Closed invalid cases
  [ComplianceCaseResolution.IoClosedInvalid]: {
    displayText: "Invalidated",
    iconType: "ban",
  },
  [ComplianceCaseResolution.SofClosedInvalid]: {
    displayText: "Invalidated",
    iconType: "ban",
  },
  [ComplianceCaseResolution.TmClosedInvalid]: {
    displayText: "Invalidated",
    iconType: "ban",
  },
  [ComplianceCaseResolution.IoClosedWaived]: {
    displayText: "GP waived",
    iconType: "ban",
  },
  // Source of Funds case resolutions
  [ComplianceCaseResolution.SofPendingReviewProposedAcceptance]: {
    displayText: "Proposed acceptance",
    iconType: "user-check",
  },
  [ComplianceCaseResolution.SofUnderReviewProposedAcceptance]: {
    displayText: "Proposed acceptance",
    iconType: "user-check",
  },
  [ComplianceCaseResolution.SofPendingReviewProposedRejection]: {
    displayText: "Proposed rejection",
    iconType: "user-magnifying-glass",
  },
  [ComplianceCaseResolution.SofUnderReviewProposedRejection]: {
    displayText: "Proposed rejection",
    iconType: "user-magnifying-glass",
  },
  [ComplianceCaseResolution.SofPendingReviewProposedInvalid]: {
    displayText: "Proposed invalidation",
    iconType: "ban",
  },
  [ComplianceCaseResolution.SofUnderReviewProposedInvalid]: {
    displayText: "Proposed invalidation",
    iconType: "ban",
  },
  [ComplianceCaseResolution.TmPendingReviewProposedInvalid]: {
    displayText: "Proposed invalidation",
    iconType: "ban",
  },
  [ComplianceCaseResolution.TmUnderReviewProposedInvalid]: {
    displayText: "Proposed invalidation",
    iconType: "ban",
  },
  [ComplianceCaseResolution.SofClosedApproved]: {
    displayText: "Accepted",
    iconType: "check-circle",
    iconColor: Colors.Success600,
  },
  [ComplianceCaseResolution.SofClosedRejected]: {
    displayText: "Rejected",
    iconType: "xmark",
    iconColor: Colors.Danger600,
  },
  // Transaction Monitoring case resolutions
  [ComplianceCaseResolution.TmPendingReviewProposedResolved]: {
    displayText: "Proposed resolved",
    iconType: "user-check",
  },
  [ComplianceCaseResolution.TmUnderReviewProposedResolved]: {
    displayText: "Proposed resolved",
    iconType: "user-check",
  },
  [ComplianceCaseResolution.TmPendingReviewProposedEscalation]: {
    displayText: "Proposed escalation",
    iconType: "user-magnifying-glass",
  },
  [ComplianceCaseResolution.TmUnderReviewProposedEscalation]: {
    displayText: "Proposed escalation",
    iconType: "user-magnifying-glass",
  },
  [ComplianceCaseResolution.TmPendingReviewDeEscalated]: {
    displayText: "Manually de-escalated",
    iconType: "arrow-down",
  },
  [ComplianceCaseResolution.TmUnderReviewDeEscalated]: {
    displayText: "Manually de-escalated",
    iconType: "arrow-down",
  },
  [ComplianceCaseResolution.TmPendingReviewCleared]: {
    displayText: "CO verdict: resolved",
    iconType: "badge-check",
  },
  [ComplianceCaseResolution.TmUnderReviewCleared]: {
    displayText: "CO verdict: resolved",
    iconType: "badge-check",
  },
  [ComplianceCaseResolution.TmPendingReviewHalted]: {
    displayText: "CO verdict: halt",
    iconType: "badge-check",
  },
  [ComplianceCaseResolution.TmUnderReviewHalted]: {
    displayText: "CO verdict: halt",
    iconType: "badge-check",
  },
  [ComplianceCaseResolution.TmEscalated]: {
    displayText: "Escalated",
    iconType: "exclamation-triangle",
    iconColor: Colors.Danger600,
  },
  [ComplianceCaseResolution.TmClosedResolved]: {
    displayText: "Resolved",
    iconType: "check-circle",
    iconColor: Colors.Success600,
  },
  [ComplianceCaseResolution.TmClosedCleared]: {
    displayText: "CO verdict: resolved",
    iconType: "check-circle",
    iconColor: Colors.Success600,
  },
  [ComplianceCaseResolution.TmClosedHalted]: {
    displayText: "CO verdict: halt",
    iconType: "xmark",
    iconColor: Colors.Danger600,
  },
  // Default cases
  [ComplianceCaseResolution.IoPendingSubmission]: {
    displayText: "--",
  },
  [ComplianceCaseResolution.IoReady]: {
    displayText: "--",
  },
  [ComplianceCaseResolution.SofReady]: {
    displayText: "--",
  },
  [ComplianceCaseResolution.TmReady]: {
    displayText: "--",
  },
  [ComplianceCaseResolution.Reopened]: {
    displayText: "Re-opened case",
    iconType: "folder-open",
  },
  [ComplianceCaseResolution.DeEscalated]: {
    displayText: "De-escalated",
    iconType: "arrow-down",
  },
  [ComplianceCaseResolution.ProposedDeficient]: {
    displayText: "Proposed LP data/doc collection",
    iconType: "user-magnifying-glass",
  },
  [ComplianceCaseResolution.ProposedEscalation]: {
    displayText: "Proposed GP escalation",
    iconType: "user-magnifying-glass",
  },
  [ComplianceCaseResolution.ProposedInvalid]: {
    displayText: "Proposed invalid",
    iconType: "ban",
  },
  [ComplianceCaseResolution.ProposedResolved]: {
    displayText: "Proposed complete",
    iconType: "user-check",
    iconColor: Colors.Success600,
  },
  [ComplianceCaseResolution.IoUnderReviewEscalated]: {
    displayText: "Escalated to GP",
    iconType: "arrow-up",
    iconColor: Colors.Danger600,
  },
  [ComplianceCaseResolution.Escalated]: {
    displayText: "Escalated to GP",
    iconType: "arrow-up",
    iconColor: Colors.Danger600,
  },
  [ComplianceCaseResolution.Resolved]: {
    displayText: "No matches detected",
    iconType: "badge-check",
    iconColor: Colors.Success600,
  },
  [ComplianceCaseResolution.Invalid]: {
    displayText: "Invalidated",
  },
  [ComplianceCaseResolution.PendingResubmission]: {
    displayText: "Requires LP data/doc submission",
    iconType: "file-edit",
  },
  [ComplianceCaseResolution.Unknown]: {
    displayText: "--",
  },
  [ComplianceCaseResolution.ReturnedToMaker]: {
    displayText: "Returned",
    iconType: "user",
  },
  [ComplianceCaseResolution.ProposedAcceptance]: {
    displayText: "GP verdict rendered",
    iconType: "badge-check",
  },
  [ComplianceCaseResolution.ProposedRejection]: {
    displayText: "GP verdict rendered",
    iconType: "badge-check",
  },
  [ComplianceCaseResolution.Accepted]: {
    displayText: "GP verdict: approved",
    iconType: "check",
    iconColor: Colors.Success600,
  },
  [ComplianceCaseResolution.Rejected]: {
    displayText: "GP verdict: rejected",
    iconType: "xmark",
    iconColor: Colors.Danger600,
  },
  [ComplianceCaseResolution.Approved]: {
    displayText: "Approved",
    iconType: "check-circle",
    iconColor: Colors.Success600,
  },
  [ComplianceCaseResolution.Denied]: {
    displayText: "Denied",
    iconType: "xmark",
    iconColor: Colors.Danger600,
  },
  [ComplianceCaseResolution.ProposedApproved]: {
    displayText: "Proposed Approved",
    iconType: "check-circle",
    iconColor: Colors.Success600,
  },
  [ComplianceCaseResolution.ProposedDenied]: {
    displayText: "Proposed Denied",
    iconType: "xmark",
    iconColor: Colors.Danger600,
  },
};
